"use client";
// components/fa/Header.tsx — Soft Modern mega-menu header for the fa site.
// Menu structure/labels come from the `navMega` message namespace (kept
// separate from `nav`, which Footer/SignInDialog/KnowledgeBaseContent still
// use) — see messages/fa.json. Verify the routes below against the real IA
// before shipping; three "راهکارها" items intentionally share /use-cases.
import Link from 'next/link';
import Image from 'next/image';
import {
    ChevronDown, Globe, Menu, Phone, X,
    Car, User, Cpu, Radio, Droplet, LayoutGrid,
    ShieldCheck, Truck, Wallet, Wrench, Wifi,
    Navigation, Cloud, Puzzle, Users, BookOpen, LifeBuoy,
    type LucideIcon,
} from "lucide-react";
import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { locales, type Locale, localePath } from '@/lib/i18n';
import { setManualLocale } from '@/lib/localePreference';
import { getPhoneNumber, toFarsiDigits, toTelHref } from '@/lib/contact';
import { SignInButtonWithDialog } from "@/components/SignInButtonWithDialog";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const locale: Locale = 'fa';

type Item = { key: string; href: string; icon: LucideIcon };
type Group = { key: string; href: string; items: Item[]; cols?: number };

const MENU: Group[] = [
    {
        key: 'products',
        href: 'products',
        cols: 2,
        items: [
            { key: 'vehicleTracker', href: 'vehicle-tracker', icon: Car },
            { key: 'personTracker', href: 'person-tracker', icon: User },
            { key: 'telemetrifa120', href: 'products/telemetrifa120', icon: Cpu },
            { key: 'telemetrifa920', href: 'products/telemetrifa920', icon: Radio },
            { key: 'fmb202', href: 'products/fmb202', icon: Droplet },
            { key: 'allProducts', href: 'products', icon: LayoutGrid },
        ],
    },
    {
        key: 'solutions',
        href: 'use-cases',
        items: [
            { key: 'safety', href: 'use-cases', icon: ShieldCheck },
            { key: 'transport', href: 'use-cases', icon: Truck },
            { key: 'costs', href: 'use-cases', icon: Wallet },
            { key: 'maintenance', href: 'pms', icon: Wrench },
            { key: 'iot', href: 'use-cases', icon: Wifi },
        ],
    },
    {
        key: 'software',
        href: 'radjoo',
        items: [
            { key: 'radjoo', href: 'radjoo', icon: Navigation },
            { key: 'saas', href: 'saas', icon: Cloud },
            { key: 'pms', href: 'pms', icon: Wrench },
            { key: 'custom', href: 'services', icon: Puzzle },
        ],
    },
    {
        key: 'company',
        href: 'customers',
        items: [
            { key: 'customers', href: 'customers', icon: Users },
            { key: 'knowledgeBase', href: 'knowledge-base', icon: BookOpen },
            { key: 'services', href: 'services', icon: LifeBuoy },
            { key: 'contact', href: 'contactus', icon: Phone },
        ],
    },
];

export default function Header() {
    const t = useTranslations('navMega');
    const tLang = useTranslations('lang');
    const [open, setOpen] = useState<number | null>(null);
    const [langOpen, setLangOpen] = useState(false);
    const [mobile, setMobile] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const langTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const phoneNumber = getPhoneNumber();
    const last = MENU.length - 1;

    const show = (i: number) => { if (timer.current) clearTimeout(timer.current); setOpen(i); };
    const hide = () => { timer.current = setTimeout(() => setOpen(null), 120); };

    // delayed close (not instant) so the cursor survives the gap between the
    // pill and the panel below it while moving to click a language
    const showLang = () => { if (langTimer.current) clearTimeout(langTimer.current); setLangOpen(true); };
    const hideLang = () => { langTimer.current = setTimeout(() => setLangOpen(false), 200); };

    // fa and the other locales have different route structures (fa has its
    // own product/tracker pages the intl site doesn't), so switching locale
    // always goes to that locale's home page rather than rewriting the path.
    const localeHref = (code: Locale) => localePath(code);

    return (
        <header className="sticky top-0 z-30 border-b border-[var(--sm-hairline)] bg-[rgba(246,248,252,.86)] backdrop-blur-[14px]">
            <div className="mx-auto flex max-w-[1260px] flex-wrap items-center justify-between gap-5 px-4 py-[13px] sm:px-8 lg:px-11">
                <Link href={localePath(locale)} className="flex items-center gap-[11px]">
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-[13px] bg-white shadow-[var(--sm-sh-1)]">
                        <Image src="/logo.png" alt={t('brand')} width={26} height={26} />
                    </span>
                    <span className="flex flex-col leading-[1.25]">
                        <span className="text-[17px] font-extrabold text-[var(--sm-ink)]">{t('brand')}</span>
                        <span className="text-[11.5px] text-[var(--sm-muted)]">{t('tagline')}</span>
                    </span>
                </Link>

                {/* desktop nav */}
                <nav className="hidden flex-wrap items-center gap-1.5 text-[14.5px] lg:flex lg:gap-2.5">
                    {MENU.map((group, i) => (
                        <div
                            key={group.key}
                            className="relative"
                            onMouseEnter={() => show(i)}
                            onMouseLeave={hide}
                            onFocus={() => show(i)}
                            onBlur={hide}
                        >
                            <Link
                                href={localePath(locale, group.href)}
                                aria-expanded={open === i}
                                className="inline-flex items-center gap-[5px] whitespace-nowrap rounded-[10px] px-[11px] py-[9px] font-semibold text-[var(--sm-ink-2)] transition hover:bg-white hover:text-[var(--sm-teal-600)] hover:shadow-[0_3px_12px_rgba(15,23,42,.07)]"
                            >
                                {t(`${group.key}.label`)}
                                <ChevronDown className="h-[13px] w-[13px] opacity-55" strokeWidth={1.9} />
                            </Link>

                            <div
                                className={[
                                    "absolute top-[calc(100%+8px)] z-40 rounded-[16px] border border-[rgba(15,23,42,.06)] bg-white px-2 pb-2.5 pt-3.5 shadow-[var(--sm-sh-3)] transition duration-150",
                                    (group.cols ?? 1) >= 2 ? "min-w-[480px]" : "min-w-[296px]",
                                    // last two panels anchor to the inline end so they never clip
                                    i >= last - 1 ? "end-0" : "start-0",
                                    open === i
                                        ? "pointer-events-auto translate-y-0 opacity-100"
                                        : "pointer-events-none -translate-y-1.5 opacity-0",
                                ].join(" ")}
                            >
                                <div className="px-2.5 pb-2.5 text-[11px] font-bold tracking-[.1em] text-[var(--sm-muted)]">
                                    {t(`${group.key}.kicker`)}
                                </div>
                                <ul
                                    className="grid list-none gap-x-1 gap-y-0.5 p-0"
                                    style={{ gridTemplateColumns: `repeat(${group.cols ?? 1}, minmax(0, 1fr))` }}
                                >
                                    {group.items.map((item) => (
                                        <li key={item.key + item.href}>
                                            <Link
                                                href={localePath(locale, item.href)}
                                                className="group/item flex items-start gap-2.5 rounded-[9px] px-2.5 py-[9px] text-[14px] font-medium text-[#1e293b] transition hover:bg-[#f1f5f9] hover:text-[var(--sm-teal-600)]"
                                            >
                                                <span className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-[var(--sm-teal-wash)] text-[var(--sm-teal-600)] transition group-hover/item:bg-[var(--sm-teal)] group-hover/item:text-white">
                                                    <item.icon className="h-[16px] w-[16px]" strokeWidth={1.8} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block">{t(`items.${item.key}.label`)}</span>
                                                    <span className="mt-0.5 block text-[11.5px] font-normal text-[var(--sm-muted)]">
                                                        {t(`items.${item.key}.hint`)}
                                                    </span>
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </nav>

                {/* right cluster: phone, language, sign-in, mobile trigger */}
                <div className="flex items-center gap-2.5">
                    {phoneNumber && (
                        <a
                            dir="ltr"
                            href={toTelHref(phoneNumber)}
                            className="hidden items-center gap-1.5 text-[13.5px] font-semibold text-[var(--sm-ink-2)] hover:text-[var(--sm-teal-600)] transition xl:flex"
                        >
                            <Phone size={15} className="text-[var(--sm-teal-600)]" />
                            <span className="sm-lat">{toFarsiDigits(phoneNumber)}</span>
                        </a>
                    )}

                    <div
                        className="relative hidden sm:block"
                        onMouseEnter={showLang}
                        onMouseLeave={hideLang}
                    >
                        <button
                            type="button"
                            onClick={() => setLangOpen((v) => !v)}
                            onFocus={showLang}
                            aria-label={t('language')}
                            aria-haspopup="listbox"
                            aria-expanded={langOpen}
                            className="inline-flex h-11 cursor-pointer items-center gap-[7px] rounded-[12px] bg-white px-[13px] text-[14px] font-bold text-[var(--sm-ink-2)] shadow-[0_3px_12px_rgba(15,23,42,.07)] transition hover:text-[var(--sm-teal-600)]"
                        >
                            <Globe className="h-[17px] w-[17px]" strokeWidth={1.8} />
                            <span className="sm-lat">{locale.toUpperCase()}</span>
                            <ChevronDown className="h-[13px] w-[13px] opacity-55" strokeWidth={1.9} />
                        </button>

                        <div
                            className={[
                                "absolute end-0 top-[calc(100%+8px)] z-40 grid min-w-[186px] gap-0.5 rounded-[16px] border border-[rgba(15,23,42,.06)] bg-white p-2 shadow-[var(--sm-sh-3)] transition duration-150",
                                langOpen
                                    ? "pointer-events-auto translate-y-0 opacity-100"
                                    : "pointer-events-none -translate-y-1.5 opacity-0",
                            ].join(" ")}
                        >
                            <ul role="listbox">
                                {locales.map((code) => (
                                    <li key={code}>
                                        <Link
                                            href={localeHref(code)}
                                            lang={code}
                                            onClick={() => {
                                                setManualLocale(code);
                                                setLangOpen(false);
                                            }}
                                            className={`flex min-h-[44px] items-center justify-between gap-3.5 rounded-[9px] px-[11px] text-[14.5px] font-semibold ${code === locale ? 'bg-[var(--sm-teal-wash)] text-[var(--sm-teal-600)]' : 'text-[#1e293b] hover:bg-[#f1f5f9]'}`}
                                        >
                                            <span>{tLang(code)}</span>
                                            <span className="sm-lat text-[11.5px] font-semibold text-[var(--sm-muted)]">
                                                {code.toUpperCase()}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <SignInButtonWithDialog
                        label={<FontAwesomeIcon icon={faCircleUser} />}
                        tooltip={t('cta')}
                        className="grid h-11 w-11 flex-none place-items-center rounded-[12px] bg-[var(--sm-ink)] text-white shadow-[0_8px_20px_rgba(15,23,42,.18)] transition hover:-translate-y-0.5 lg:hidden lg:h-auto lg:w-auto lg:rounded-[12px] lg:px-[21px] lg:py-[11px] lg:text-[14px] lg:font-bold"
                    />
                    <SignInButtonWithDialog
                        label={t('cta')}
                        tooltip={t('cta')}
                        className="hidden items-center gap-2 rounded-[12px] bg-[var(--sm-ink)] px-[21px] py-[11px] text-[14px] font-bold text-white shadow-[0_8px_20px_rgba(15,23,42,.18)] transition hover:-translate-y-0.5 lg:inline-flex"
                    />

                    <button
                        type="button"
                        onClick={() => setMobile((v) => !v)}
                        aria-label={t('menu')}
                        aria-expanded={mobile}
                        className="grid h-11 w-11 place-items-center rounded-[12px] bg-white shadow-[var(--sm-sh-1)] lg:hidden"
                    >
                        {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* mobile drawer — accordion, 44px+ targets */}
            {mobile && (
                <div className="border-t border-[var(--sm-hairline)] bg-[var(--sm-bg)] px-4 pb-5 pt-2 lg:hidden">
                    {MENU.map((group, i) => (
                        <details key={group.key} open={i === 0} className="border-b border-[var(--sm-hairline)] py-1">
                            <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between px-1 text-[15px] font-bold text-[var(--sm-ink)]">
                                {t(`${group.key}.label`)}
                                <ChevronDown className="h-4 w-4 opacity-60" />
                            </summary>
                            <ul className="grid list-none gap-0.5 p-0 pb-2">
                                {group.items.map((item) => (
                                    <li key={item.key + item.href}>
                                        <Link
                                            href={localePath(locale, item.href)}
                                            onClick={() => setMobile(false)}
                                            className="flex min-h-[46px] items-center gap-2.5 rounded-[10px] px-2.5 text-[14.5px] font-medium text-[var(--sm-ink-2)] hover:bg-white"
                                        >
                                            <span className="grid h-7 w-7 flex-none place-items-center rounded-[8px] bg-[var(--sm-teal-wash)] text-[var(--sm-teal-600)]">
                                                <item.icon className="h-[14px] w-[14px]" strokeWidth={1.8} />
                                            </span>
                                            {t(`items.${item.key}.label`)}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ))}

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-b border-[var(--sm-hairline)] pb-4">
                        {locales.map((code) => (
                            <Link
                                key={code}
                                href={localeHref(code)}
                                lang={code}
                                onClick={() => setManualLocale(code)}
                                className={`flex min-h-[44px] items-center rounded-[11px] px-4 text-[14.5px] font-semibold ${code === locale ? 'bg-[var(--sm-teal-wash)] text-[var(--sm-teal-600)]' : 'bg-white text-[var(--sm-ink-2)]'}`}
                            >
                                {tLang(code)}
                            </Link>
                        ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2 border-b border-[var(--sm-hairline)] pb-4">
                        {phoneNumber && (
                            <a dir="ltr" href={toTelHref(phoneNumber)} className="flex items-center gap-1.5 text-[13.5px] font-semibold text-[var(--sm-ink-2)]">
                                <Phone size={15} className="text-[var(--sm-teal-600)]" />
                                <span className="sm-lat">{toFarsiDigits(phoneNumber)}</span>
                            </a>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
